import { Path } from "@ostack.tech/kform";
import { type ReactNode, useMemo } from "react";

import { FormContext, type FormContextValue } from "../contexts/FormContext.ts";
import { useFormContext } from "../hooks/useFormContext.ts";
import { useResolvedPath } from "../hooks/useResolvedPath.ts";

/** Properties of the current path component. */
export interface CurrentPathProps {
  /**
   * Path to set as the new current path for all children of this component.
   *
   * This path will be resolved against the current path.
   *
   * @default Path.CURRENT
   */
  path?: Path | string;
  children?: ReactNode;
}

/**
 * Component used to specify a new current path for its children.
 *
 * This component must be rendered within a form context. The provided path will
 * be resolved against the current path in context.
 */
export function CurrentPath({
  path = Path.CURRENT,
  children,
}: CurrentPathProps) {
  const formContext = useFormContext();
  const resolvedPath = useResolvedPath(path);

  const newFormContext = useMemo<FormContextValue>(
    () => ({
      formManager: formContext.formManager,
      currentPath: resolvedPath,
      controller: formContext.controller,
    }),
    [formContext.controller, formContext.formManager, resolvedPath],
  );

  return (
    <FormContext.Provider value={newFormContext}>
      {children}
    </FormContext.Provider>
  );
}
